/**
 * Lab-runs store (T10 — plan §10.2).
 *
 * Two responsibilities:
 *
 *   * Hydrate from ``GET /api/test-runs`` and refresh on a fixed
 *     poll cadence so an in-flight ``running`` row transitions to
 *     ``passed`` / ``failed`` without a manual reload.
 *   * Track the user's currently-selected run for future drill-in
 *     panels (live screenshot, console tail, …) — those land with
 *     the ``LabRunPage`` follow-up.
 *
 * Polling is opt-in via :func:`startPolling` / :func:`stopPolling`.
 * Tests inject a fake clock by passing ``intervalMs=0`` so the
 * polling loop becomes single-shot.
 */
import { create } from 'zustand'

import { api, ApiError } from '@/lib/api'
import type { LabRunSummary } from '@/types/labRuns'

export interface LabRunState {
  runs: LabRunSummary[]
  loading: boolean
  /** ``"disabled"`` when the backend route 404s (Test Lab is OFF). */
  disabled: boolean
  /** Last error from the most recent fetch. */
  error: string | null
  selectedRunId: string | null
  /** Internal interval id so :func:`stopPolling` can clear it. */
  _pollHandle: ReturnType<typeof setInterval> | null

  refresh: () => Promise<void>
  selectRun: (runId: string | null) => void
  startPolling: (intervalMs?: number) => void
  stopPolling: () => void
  reset: () => void
}

const DEFAULT_POLL_MS = 2_000

const initialState: Pick<
  LabRunState,
  'runs' | 'loading' | 'disabled' | 'error' | 'selectedRunId' | '_pollHandle'
> = {
  runs: [],
  loading: false,
  disabled: false,
  error: null,
  selectedRunId: null,
  _pollHandle: null,
}

export const useLabRunStore = create<LabRunState>((set, get) => ({
  ...initialState,

  reset: () => {
    const handle = get()._pollHandle
    if (handle) clearInterval(handle)
    set({ ...initialState })
  },

  selectRun: (runId) => set({ selectedRunId: runId }),

  refresh: async () => {
    set({ loading: true })
    try {
      const runs = await api.testRuns.list()
      set({ runs, loading: false, disabled: false, error: null })
    } catch (err) {
      // 404 from the listing route ⇒ Test Lab is not enabled on
      // this backend. Render the disabled state rather than an
      // error toast.
      if (err instanceof ApiError && err.status === 404) {
        set({ runs: [], loading: false, disabled: true, error: null })
        return
      }
      set({
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      })
    }
  },

  startPolling: (intervalMs = DEFAULT_POLL_MS) => {
    // Always trigger an immediate refresh so the table populates
    // without waiting one full interval.
    void get().refresh()
    if (intervalMs <= 0) return
    const existing = get()._pollHandle
    if (existing) clearInterval(existing)
    const handle = setInterval(() => {
      void get().refresh()
    }, intervalMs)
    set({ _pollHandle: handle })
  },

  stopPolling: () => {
    const handle = get()._pollHandle
    if (handle) {
      clearInterval(handle)
      set({ _pollHandle: null })
    }
  },
}))
